/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "AI Decisioning overview", description = "AI Decisioning automates campaign decisions at the individual user level — choosing the best message, channel, and timing for each person based on what's working.", lastUpdatedDate = new Date(1788220800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "AI Decisioning overview",
        description: "AI Decisioning automates campaign decisions at the individual user level — choosing the best message, channel, and timing for each person based on what's working.",
        lastUpdatedDate: "Tue Sep 01 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "How agents work",
          "depth": 0,
          "id": "how-agents-work"
        }, {
          "value": "Core components",
          "depth": 0,
          "id": "core-components"
        }, {
          "value": "Agents and messages",
          "depth": 1,
          "id": "agents-and-messages"
        }, {
          "value": "Optimization tools",
          "depth": 1,
          "id": "optimization-tools"
        }, {
          "value": "Measurement and QA",
          "depth": 1,
          "id": "measurement-and-qa"
        }, {
          "value": "Get started",
          "depth": 0,
          "id": "get-started"
        }, {
          "value": "For data teams and platform admins",
          "depth": 1,
          "id": "for-data-teams-and-platform-admins"
        }, {
          "value": "For marketers",
          "depth": 1,
          "id": "for-marketers"
        }, {
          "value": "When to use AI Decisioning",
          "depth": 0,
          "id": "when-to-use-ai-decisioning"
        }, {
          "value": "Use AI Decisioning with other Hightouch products",
          "depth": 0,
          "id": "use-ai-decisioning-with-other-hightouch-products"
        }, {
          "value": "Use cases",
          "depth": 0,
          "id": "use-cases"
        }, {
          "value": "Permissions",
          "depth": 0,
          "id": "permissions"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      a: "a",
      h4: "h4",
      ol: "ol",
      li: "li",
      img: "img",
      h3: "h3",
      ul: "ul"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Audience"
            }), _jsx(_components.th, {
              children: "How you'll use this article"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Marketing"
              })
            }), _jsx(_components.td, {
              children: "Understand what AI Decisioning does, how agents work, and how to set up your first adaptive campaign."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Data teams"
              })
            }), _jsx(_components.td, {
              children: "Understand what data AI Decisioning needs, how to configure your workspace, and how agents connect to destinations."
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "AI Decisioning (AID) automates campaign decisions at the individual user level. You define agents that combine an audience, goals, and messages — then AID learns which message, channel, and timing perform best for each person."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Most lifecycle campaigns are built with static rules: if a user does X, send message Y. That can work for simple campaigns, but it becomes harder to manage as audiences, messages, and channels increase."
      }), "\n", _jsx(_components.p, {
        children: "AI Decisioning uses event data to learn which messages, channels, and timing work best for each user. Instead of writing rules for every scenario, you define what success looks like — purchases, sign-ups, engagement — and AID uses performance data to choose the message, channel, and timing for each person."
      }), "\n", _jsxs(_components.p, {
        children: ["The core unit is an ", _jsx(_components.strong, {
          children: "agent"
        }), ". Each agent combines everything a campaign needs to learn and improve: an audience, goals, messages, scheduling rules, and guardrails. Once running, the agent uses event data as feedback and continuously adjusts which messages, channels, and timing it uses."]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Agents operate entirely within the inputs and rules you define. They don't create new content or act outside your configuration."
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: [_jsx(_components.a, {
            href: "https://architecture.hightouch.com/?from=docs&product=ai-decisioning&utm_source=hightouch-docs&utm_medium=referral&utm_campaign=architecture-explorer&utm_content=ai-decisioning",
            children: _jsx(_components.strong, {
              children: "Explore the interactive architecture diagram"
            })
          }), " — see how data flows through your stack and where AI Decisioning runs."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "example-onboarding-campaign-that-learns-what-converts",
        children: "Example: Onboarding campaign that learns what converts"
      }), "\n", _jsx(_components.p, {
        children: "You want new users to complete their first purchase within 14 days of signing up. Instead of building a fixed drip sequence, you create an agent with a \"first purchase\" goal, add several message variants across email and push, and let AID decide which variant and channel each user gets. Over time, the agent learns that discount-forward messages work better for price-sensitive segments while product-focused messages convert power users — without you writing rules for each case."
      }), "\n", _jsx(_components.h4, {
        id: "example-win-back-campaign-with-smart-suppression",
        children: "Example: Win-back campaign with smart suppression"
      }), "\n", _jsx(_components.p, {
        children: "Lapsed users haven't engaged in 60 days. You create an agent targeting that audience with a re-engagement goal, add variants with different incentive levels, and enable Smart suppression to skip users unlikely to respond. AID learns which incentive level and channel re-activates each type of user, while suppression reduces wasted sends."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "how-agents-work",
        children: "How agents work"
      }), "\n", _jsx(_components.p, {
        children: "Each agent follows a continuous loop:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Collect inputs"
          }), " — Audience, goals, messages, and guardrails."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Decide"
          }), " — Which message to send, when, and through which channel."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Deliver"
          }), " — Send through your connected destinations (Braze, Iterable, Salesforce Marketing Cloud)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Measure"
          }), " — Record outcomes such as purchases or clicks."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Learn"
          }), " — Update future decisions based on what worked."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "This process runs automatically for every user. AID uses reinforcement learning to balance exploration (testing new options) with optimization (sending what performs best). Unlike static rules or journeys, AID adjusts automatically to seasonality, creative changes, and audience behavior shifts."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/overview/aid-overview.png",
          alt: "AID Overview"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "core-components",
        children: "Core components"
      }), "\n", _jsx(_components.h3, {
        id: "agents-and-messages",
        children: "Agents and messages"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "/ai-decisioning/agents",
          children: "Agents"
        }), " are the foundation — each one combines an audience, goals, and messages into a single adaptive campaign. After creating an agent, add ", _jsx(_components.a, {
          href: "/ai-decisioning/messages",
          children: "messages"
        }), " sourced from your destination (Braze, Iterable, or SFMC) and define variants the agent can evaluate. Use ", _jsx(_components.a, {
          href: "/ai-decisioning/tags",
          children: "tags"
        }), " to label messages by creative theme, offer type, or content category so you can compare performance across dimensions."]
      }), "\n", _jsx(_components.h3, {
        id: "optimization-tools",
        children: "Optimization tools"
      }), "\n", _jsxs(_components.p, {
        children: ["Enable ", _jsx(_components.strong, {
          children: "Smart suppression"
        }), " to reduce low-impact sends using predictive performance data. Run ", _jsx(_components.a, {
          href: "/ai-decisioning/content-analysis",
          children: "content analysis"
        }), " to identify overlap, gaps, and opportunities across your message variants."]
      }), "\n", _jsx(_components.h3, {
        id: "measurement-and-qa",
        children: "Measurement and QA"
      }), "\n", _jsxs(_components.p, {
        children: ["Review agent performance with ", _jsx(_components.a, {
          href: "/ai-decisioning/insights",
          children: "Insights"
        }), ", which shows conversion breakdowns by audience, creative performance by message or tag, timing insights, and lift metrics. Use the ", _jsx(_components.a, {
          href: "/ai-decisioning/inspector",
          children: "Inspector"
        }), " to preview sends, confirm eligibility, and troubleshoot delivery issues. Before launching, walk through the ", _jsx(_components.a, {
          href: "/ai-decisioning/qa-guide",
          children: "QA guide"
        }), " for channel-specific validation checklists."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/insights/aid-insights-summary.png",
          alt: "AID Insights"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "get-started",
        children: "Get started"
      }), "\n", _jsx(_components.p, {
        children: "AI Decisioning requires initial configuration from data teams before marketers can create agents. This setup is typically done once."
      }), "\n", _jsx(_components.h3, {
        id: "for-data-teams-and-platform-admins",
        children: "For data teams and platform admins"
      }), "\n", _jsx(_components.p, {
        children: "Complete these steps in order. Marketers cannot create agents until AI Decisioning is configured and at least one destination is connected."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Step"
            }), _jsx(_components.th, {
              children: "What to do"
            }), _jsx(_components.th, {
              children: "Why"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "1"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/ai-decisioning/configuration",
                children: "Configure AI Decisioning settings"
              })
            }), _jsx(_components.td, {
              children: "Define channels, scheduling limits, and shared defaults."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "2"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/ai-decisioning/prepare-data",
                children: "Prepare data for AID"
              })
            }), _jsx(_components.td, {
              children: "Define audience models, structure event data, and prepare tables for decisioning."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "3"
            }), _jsxs(_components.td, {
              children: ["Connect a destination: ", _jsx(_components.a, {
                href: "/ai-decisioning/braze",
                children: "Braze"
              }), ", ", _jsx(_components.a, {
                href: "/ai-decisioning/iterable",
                children: "Iterable"
              }), ", or ", _jsx(_components.a, {
                href: "/ai-decisioning/sfmc",
                children: "Salesforce Marketing Cloud"
              })]
            }), _jsx(_components.td, {
              children: "Connect your ESP or messaging platform so AID can deliver messages."
            })]
          })]
        })]
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["Once AI Decisioning is configured and a destination is connected, marketers\ncan start ", _jsx(_components.a, {
            href: "https://app.hightouch.com/ai/flows?from=docs",
            children: "creating agents"
          }), "\nimmediately."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "for-marketers",
        children: "For marketers"
      }), "\n", _jsxs(_components.p, {
        children: ["Once your data team has completed the setup above, start by ", _jsx(_components.a, {
          href: "/ai-decisioning/agents",
          children: "creating your first agent"
        }), ". Define a target audience and a single measurable goal — most teams start with one goal like reactivation or first purchase and expand as performance data accumulates."]
      }), "\n", _jsxs(_components.p, {
        children: ["From there, ", _jsx(_components.a, {
          href: "/ai-decisioning/messages",
          children: "add messages"
        }), " from your connected destination, define variants, and launch. Use the ", _jsx(_components.a, {
          href: "/ai-decisioning/qa-guide",
          children: "QA guide"
        }), " to validate before going live, then monitor performance in ", _jsx(_components.a, {
          href: "/ai-decisioning/insights",
          children: "Insights"
        }), " as your agent learns."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "when-to-use-ai-decisioning",
        children: "When to use AI Decisioning"
      }), "\n", _jsxs(_components.p, {
        children: ["Use AI Decisioning for lifecycle or retention campaigns where users may need different messages, channels, or timing based on their behavior. For campaigns where you need full control over sequencing and timing, use ", _jsx(_components.a, {
          href: "/customer-studio/journeys",
          children: "Journeys"
        }), " in Customer Studio instead."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Scenario"
            }), _jsx(_components.th, {
              children: "Recommended approach"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Automate which message, channel, and timing each user receives"
            }), _jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "AI Decisioning"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Onboard, win back, cross-sell, or retain with adaptive campaigns"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.strong, {
                children: "AI Decisioning"
              }), " ", _jsx(_components.a, {
                href: "/ai-decisioning/agents",
                children: "Agents"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Reduce wasted sends by suppressing low-likelihood-to-convert users"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.strong, {
                children: "AI Decisioning"
              }), " Smart suppression"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Build audiences from warehouse data and sync to destinations"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/customer-studio/overview",
                children: "Customer Studio"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Run a multi-step workflow with fixed sequencing and branching"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.a, {
                href: "/customer-studio/journeys",
                children: "Journeys"
              }), " in Customer Studio"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Measure experiment results and track KPIs"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/campaign-intelligence/overview",
                children: "Intelligence"
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "use-ai-decisioning-with-other-hightouch-products",
        children: "Use AI Decisioning with other Hightouch products"
      }), "\n", _jsx(_components.p, {
        children: "AI Decisioning learns how to reach each person most effectively. It uses audiences and event data from your warehouse, then chooses the best message, channel, and timing for each customer and sends through your connected messaging tools."
      }), "\n", _jsx(_components.h4, {
        id: "connect-your-audiences-events-and-messages",
        children: "Connect your audiences, events, and messages"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsxs(_components.strong, {
            children: ["Audiences from ", _jsx(_components.a, {
              href: "/customer-studio/overview",
              children: "Customer Studio"
            })]
          }), " — Agents target Customer Studio audiences as their eligible population. Audience membership updates automatically as warehouse data changes."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsxs(_components.strong, {
            children: ["Event data from your ", _jsx(_components.a, {
              href: "/sources/overview",
              children: "connected source"
            })]
          }), " — AID uses behavioral events like purchases, clicks, and app opens as feedback signals to learn what drives conversions."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Messages from connected destinations"
          }), " — Messages are sourced from your ESP or messaging platform (", _jsx(_components.a, {
            href: "/ai-decisioning/braze",
            children: "Braze"
          }), ", ", _jsx(_components.a, {
            href: "/ai-decisioning/iterable",
            children: "Iterable"
          }), ", or ", _jsx(_components.a, {
            href: "/ai-decisioning/sfmc",
            children: "Salesforce Marketing Cloud"
          }), "). To send to another destination, use a ", _jsx(_components.a, {
            href: "/ai-decisioning/custom-destinations",
            children: "custom channel"
          }), ". AID evaluates variants and controls which version is sent — it does not modify your base content."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "what-aid-delivers",
        children: "What AID delivers"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Optimized sends to destinations"
          }), " — AID delivers the best message, channel, and timing for each user through your connected ESP or messaging platform."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsxs(_components.strong, {
            children: ["Performance data to ", _jsx(_components.a, {
              href: "/ai-decisioning/insights",
              children: "Insights"
            })]
          }), " — Conversion breakdowns, creative performance, timing insights, and lift metrics are available for ongoing analysis and refinement."]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "use-cases",
        children: "Use cases"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Use case"
            }), _jsx(_components.th, {
              children: "Example goal"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Onboarding"
              })
            }), _jsx(_components.td, {
              children: "Help new users complete a first purchase or app setup"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Win-back"
              })
            }), _jsx(_components.td, {
              children: "Re-engage lapsed users with personalized incentives"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Cross-sell or upsell"
              })
            }), _jsx(_components.td, {
              children: "Recommend complementary products or upgrades"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Referral"
              })
            }), _jsx(_components.td, {
              children: "Encourage existing customers to invite friends"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Retention"
              })
            }), _jsx(_components.td, {
              children: "Keep engaged users active with timely, relevant messages"
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "Each agent focuses on a single measurable outcome and continuously improves performance over time."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/overview/aid-marketing-shift.png",
          alt: "AID Marketing Shift"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "permissions",
        children: "Permissions"
      }), "\n", _jsxs(_components.p, {
        children: ["AI Decisioning access is managed through ", _jsx(_components.a, {
          href: "/workspace-management/roles",
          children: "workspace roles"
        }), ", like other Hightouch products. Every workspace member can view AI Decisioning resources. Editing message content—copy, variables and variants, tags, and collections—requires the ", _jsx(_components.strong, {
          children: "Edit content"
        }), " capability on a custom role; managing agents themselves—creation, lifecycle, goals, and configuration—requires ", _jsx(_components.strong, {
          children: "Manage agents"
        }), ". Roles can also limit which agents a group works with by parent model. See ", _jsx(_components.a, {
          href: "/ai-decisioning/permissions",
          children: "AI Decisioning permissions"
        }), " for the full permission matrix."]
      })]
    });
  }
}
export default MDXContent;
